<script setup lang="ts">
  import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import { useModal } from '@/composition/use-modal';
  import {
    applyBulkPrice,
    IBulkPricePreviewLine,
    lowestSelectedPrice,
    parseBulkValue,
    roundingSteps,
    TBulkPriceDirection,
    TBulkPriceUnit,
  } from '@/domain/catalog/bulk-price';
  import { currencySymbol, formatMoney } from '@/domain/store-setup/money';
  import { IBulkPriceChange } from '@/interfaces/ICatalogV2';
  import { IItem } from '@/interfaces/IItem';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Cambiar precio de {n} productos» (plan §5.10): raise or lower by a
   * percentage or an amount, optionally rounded, with the «Así quedan»
   * preview. Resolves the price changes to send, or null.
   */
  const props = defineProps<{
    /** The vue-js-modal name it was opened with. */
    dialogName: string;
    items: IItem[];
    currency: string;
    country: string;
  }>();

  const modals = useModal();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const root = ref<HTMLElement | null>(null);
  const valueId = nextDomId('bulk-price-value');
  const roundingId = nextDomId('bulk-price-rounding');

  const direction = ref<TBulkPriceDirection>('up');
  const unit = ref<TBulkPriceUnit>('percent');
  const valueText = ref('');
  /** null = «No redondear». */
  const rounding = ref<number | null>(null);

  const directions = computed(() => [
    { id: 'up', label: t('menuPage.bulkPrice.up') },
    { id: 'down', label: t('menuPage.bulkPrice.down') },
  ]);
  const units = computed(() => [
    { id: 'percent', label: '%' },
    { id: 'amount', label: currencySymbol(props.currency) },
  ]);

  const steps = computed(() =>
    roundingSteps(props.currency, lowestSelectedPrice(props.items))
  );
  // A step no longer offered (another unit or currency) falls back to none.
  watch(steps, (next) => {
    if (rounding.value !== null && !next.includes(rounding.value))
      rounding.value = null;
  });

  const value = computed(() => parseBulkValue(valueText.value, unit.value));
  const valueError = computed(() =>
    valueText.value.trim() && value.value === null
      ? t('menuPage.bulkPrice.invalidValue')
      : undefined
  );

  const result = computed(() =>
    value.value === null
      ? null
      : applyBulkPrice(props.items, {
          direction: direction.value,
          unit: unit.value,
          value: value.value,
          rounding: rounding.value,
        })
  );
  const canApply = computed(
    () =>
      !!result.value && !result.value.blocked && result.value.changes.length > 0
  );

  function money(amount: number): string {
    return formatMoney(amount, props.currency, props.country);
  }

  /** «Con descuento: $ 900 → $ 990». */
  function discountText(line: IBulkPricePreviewLine): string | null {
    if (line.discountBefore === null) return null;
    return t('menuPage.bulkPrice.discountLine', {
      before: money(line.discountBefore),
      after: money(line.discountAfter ?? line.discountBefore),
    });
  }

  function lineProblem(line: IBulkPricePreviewLine): string | null {
    if (line.problem) return t(`menuPage.bulkPrice.problem.${line.problem}`);
    if (line.wrongSide)
      return t(
        direction.value === 'up'
          ? 'menuPage.bulkPrice.wrongSideUp'
          : 'menuPage.bulkPrice.wrongSideDown'
      );
    return null;
  }

  function answer(changes: IBulkPriceChange[] | null) {
    // Closes and unmounts the dialog: nothing may run after this.
    modals.dialogSubmit(props.dialogName, changes);
  }

  function apply() {
    if (canApply.value && result.value) answer(result.value.changes);
  }

  onMounted(() => {
    root.value
      ?.querySelector<HTMLElement>(`#${valueId}`)
      ?.focus({ preventScroll: true });
  });
</script>

<template>
  <div
    ref="root"
    class="v2-catalog-bulk-price-dialog"
    role="dialog"
    :aria-label="
      tc('menuPage.bulkPrice.title', items.length, { n: items.length })
    "
    @keydown.esc.stop="answer(null)"
  >
    <h2 class="dialog-title">
      {{ tc('menuPage.bulkPrice.title', items.length, { n: items.length }) }}
    </h2>

    <div class="dialog-controls">
      <PosUiSegmented
        class="control-direction"
        :value="direction"
        :items="directions"
        @input="direction = $event === 'down' ? 'down' : 'up'"
      />
      <PosUiSegmented
        class="control-unit"
        :value="unit"
        :items="units"
        @input="unit = $event === 'amount' ? 'amount' : 'percent'"
      />
    </div>

    <PosUiField
      class="dialog-value"
      :label="t('menuPage.bulkPrice.value')"
      :input-id="valueId"
      :error="valueError"
    >
      <PosUiInput
        :id="valueId"
        :value="valueText"
        inputmode="decimal"
        autocomplete="off"
        :prefix="unit === 'amount' ? currencySymbol(currency) : undefined"
        :suffix="unit === 'percent' ? '%' : undefined"
        :placeholder="unit === 'percent' ? '10' : '500'"
        @input="valueText = $event"
        @enter="apply"
      />
    </PosUiField>

    <div v-if="steps.length" class="dialog-rounding">
      <span :id="roundingId" class="rounding-label">{{
        t('menuPage.bulkPrice.rounding')
      }}</span>
      <div
        class="rounding-chips"
        role="radiogroup"
        :aria-labelledby="roundingId"
      >
        <PosUiChip
          class="rounding-chip"
          radio
          :active="rounding === null"
          @click="rounding = null"
          >{{ t('menuPage.bulkPrice.noRounding') }}</PosUiChip
        >
        <PosUiChip
          v-for="step in steps"
          :key="step"
          class="rounding-chip"
          radio
          :active="rounding === step"
          @click="rounding = step"
          >{{ money(step) }}</PosUiChip
        >
      </div>
    </div>

    <div class="dialog-preview">
      <span class="preview-label">{{ t('menuPage.bulkPrice.preview') }}</span>
      <p v-if="!result" class="preview-empty">
        {{ t('menuPage.bulkPrice.previewEmpty') }}
      </p>
      <ul v-else class="preview-list">
        <li
          v-for="row in result.preview"
          :key="row.itemId"
          class="preview-item"
        >
          <span class="item-name">{{ row.name }}</span>
          <div
            v-for="(line, index) in row.lines"
            :key="index"
            class="item-line"
            :class="{
              'is-problem': !!line.problem,
              'is-warning': line.wrongSide,
            }"
          >
            <span v-if="line.label" class="line-label">{{ line.label }}</span>
            <span class="line-prices">
              {{ money(line.before) }} →
              <strong>{{ money(line.after) }}</strong>
            </span>
            <span v-if="discountText(line)" class="line-discount">{{
              discountText(line)
            }}</span>
            <span v-if="lineProblem(line)" class="line-problem">{{
              lineProblem(line)
            }}</span>
          </div>
        </li>
      </ul>
    </div>

    <p v-if="result && result.blocked" class="dialog-blocked" role="alert">
      {{ t('menuPage.bulkPrice.blocked') }}
    </p>

    <div class="dialog-actions">
      <PosUiButton color="neutral" variant="ghost" @click="answer(null)">{{
        $t('cancel')
      }}</PosUiButton>
      <PosUiButton class="dialog-apply" :disabled="!canApply" @click="apply">{{
        t('menuPage.bulkPrice.apply')
      }}</PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-bulk-price-dialog {
    @apply tw-flex tw-flex-col tw-bg-surface tw-px-5 tw-pb-4 tw-pt-5;

    .dialog-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }

    // Margins, not flex gap (Chrome < 84).
    .dialog-controls {
      @apply -tw-mb-2 -tw-mr-2 tw-mt-4 tw-flex tw-flex-wrap;

      .control-direction,
      .control-unit {
        @apply tw-mb-2 tw-mr-2;
      }
    }

    .dialog-value {
      @apply tw-mt-4;
      max-width: 220px;
    }

    .dialog-rounding {
      @apply tw-mt-4;

      .rounding-label {
        @apply tw-mb-1.5 tw-block tw-text-sm tw-font-medium tw-text-neutral-900;
      }

      .rounding-chips {
        @apply -tw-mb-2 -tw-mr-2 tw-flex tw-flex-wrap;
      }

      .rounding-chip {
        @apply tw-mb-2 tw-mr-2;
      }
    }

    .dialog-preview {
      @apply tw-mt-4;

      .preview-label {
        @apply tw-mb-1.5 tw-block tw-text-sm tw-font-medium tw-text-neutral-900;
      }

      .preview-empty {
        @apply tw-m-0 tw-text-sm tw-text-neutral-500;
      }

      .preview-list {
        @apply tw-m-0 tw-list-none tw-overflow-y-auto tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-p-0;
        max-height: 260px;
      }

      .preview-item {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-px-3 tw-py-2;

        &:first-child {
          @apply tw-border-t-0;
        }

        .item-name {
          @apply tw-block tw-truncate tw-text-sm tw-font-medium tw-text-neutral-900;
        }
      }

      .item-line {
        @apply tw-mt-0.5 tw-flex tw-flex-wrap tw-items-baseline tw-text-sm tw-tabular-nums tw-text-neutral-600;

        .line-label {
          @apply tw-mr-2 tw-text-neutral-500;
        }

        .line-prices strong {
          @apply tw-font-semibold tw-text-neutral-900;
        }

        .line-discount {
          @apply tw-ml-2 tw-text-xs tw-text-neutral-500;
        }

        .line-problem {
          @apply tw-w-full tw-text-xs;
        }

        &.is-warning .line-problem {
          @apply tw-text-amber-800;
        }

        &.is-problem .line-problem {
          @apply tw-text-red-600;
        }
      }
    }

    .dialog-blocked {
      @apply tw-m-0 tw-mt-3 tw-text-sm tw-text-red-600;
    }

    .dialog-actions {
      @apply tw-mt-4 tw-flex tw-items-center tw-justify-end;

      .dialog-apply {
        @apply tw-ml-2;
      }
    }
  }
</style>
